<template>
  <wizard
    first-step="personalDetails"
    class="wizard-payment-arrangement"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="paymentArrangement"
      :model="formData"
      next-step="review"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.paymentArrangement.title }}</h2>
        <slot name="payment-arrangement-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.headings.invoiceNumbers }}</h3>

        <FormulateInput
          type="group"
          name="invoices"
          :help="i18n.form.help.invoices"
          :add-label="i18n.form.addlabel.invoice"
          :repeatable="true"
          validation="bail|min:1,length|max:3,length|unique:invoiceNumber"
          :validation-messages="i18n.form.validations.invoices"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->
            <FormulateInput
              type="text"
              name="invoiceNumber"
              :label="i18n.form.labels.invoiceNumber"
              validation="bail|required:trim|number|min:6,length|max:10,length"
              :validation-messages="invoiceValidation"
            />
          </div>
        </FormulateInput>

        <h3>{{ i18n.form.headings.payments }}</h3>

        <FormulateInput
          type="select"
          name="months"
          :options="optionsForAmountOfMonths"
          :label="i18n.form.labels.months"
          validation="required"
          :validation-messages="i18n.form.validations.months"
        />

        <fields-date-picker
          :i18n="i18n"
          :min-date="minDateForFirstPayment"
          :max-date="maxDateForFirstPayment"
          :date-validation="
            'bail|required|before:' +
              dateAfterMaxDateForFirstPayment +
              '|after:' +
              dateBeforeMinDateForFirstPayment
          "
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="paymentArrangement"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <td>{{ i18n.form.labels.reviewName }}</td>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewEmail }}</td>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewPhoneNumber }}</td>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewCustomerNumber }}</td>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>
          <tr
            v-for="(invoice, index) in resultingData.paymentArrangement.invoices"
            :key="index"
          >
            <td>
              <span v-if="!index">{{ i18n.form.labels.reviewInvoiceNumbers }}</span>
              <span v-else>&nbsp;</span>
            </td>
            <td>{{ invoice.invoiceNumber }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewPaymentMonths }}</td>
            <td>{{ resultingData.paymentArrangement.months }}</td>
          </tr>
          <tr>
            <td>{{ i18n.form.labels.reviewPaymentFirstTerm }}</td>
            <td>{{ moment(resultingData.paymentArrangement.date).format('DD-MM-YYYY') }}</td>
          </tr>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
import Wizard from '@/components/wizards/components/Wizard.vue';
import WizardStep from '@/components/wizards/components/WizardStep.vue';
import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
import utils from '@/lib/utils';
import axios from 'axios';
import moment from 'moment';
import getQueryParams from '@/lib/formulate/getFormQueryParam';

export default {
  name: 'WizardPaymentArrangement',
  components: {
    FieldsDatePicker,
    Wizard,
    WizardStep,
    WizardStepIntro,
  },
  props: {
    i18n: {
      type: Object,
      default: null,
    },
    prefillData: {
      type: Object,
      default: () => { },
    },
    locale: {
      type: String,
      default: () => 'nl',
    },
    label: {
      type: String,
      default: 'Partou',
    },
    minDateForFirstPayment: {
      type: String,
      default: () => null,
    },
    maxDateForFirstPayment: {
      type: String,
      default: () => null,
    },
    successPageUrl: {
      type: String,
      default: () => '/',
    },
    debtorNumberRange:{
      type: Object,
      default: () => {
        return {
          min: 0,
          max: 0,
        }
      }
    }
  },
  data() {
    return {
      formData: {
        personalDetails: this.$store.state.wizard.personalDetails,
        paymentArrangement: {},
      },
      request: {
        isSending: false,
        errorSending: false,
      },
    };
  },
  computed: {
    moment() {
      return moment;
    },
    dateBeforeMinDateForFirstPayment() {
      return utils.date.getDate(new Date(this.minDateForFirstPayment));
    },
    dateAfterMaxDateForFirstPayment() {
      return utils.date.getDate(new Date(this.maxDateForFirstPayment), 1);
    },
    optionsForAmountOfMonths() {
      const amountOfMonths = [];
      for (let i = 2; i < this.maxAmountOfMonths + 1; i++) {
        amountOfMonths.push({ value: i, label: i });
      }
      return amountOfMonths;
    },
    maxAmountOfMonths() {
      const maxPeriod = new URLSearchParams(window.location.search).get('max-period');
      if (maxPeriod) return maxPeriod === 'year' ? 12 : 8;

      const {min,max} = this.debtorNumberRange
      const number = parseInt(this.formData.personalDetails.debtorNumber)

      if (!!number &&  typeof number === 'number' && !!min && !!max) {
        return number >= min && number <= max ? 12 : 8
      }

      return 8
    },
    resultingData() {
      return {
        label: this.label,
        locale: this.locale,
        personalDetails: this.formData.personalDetails,
        paymentArrangement: this.formData.paymentArrangement,
      };
    },
    invoiceValidation() {
      const { max, required, number } = this.i18n.form.validations.invoiceNumber
      return {
        min: max,
        max,
        number,
        required
      }
    }
  },
  created () {
    this.formData.personalDetails = {
      ...this.formData.personalDetails,
      ...getQueryParams(this.prefillData)
    }
  },
  methods: {
    submitPersonalDetails() {
      this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
      return 'paymentArrangement';
    },
    sendRequest() {
      this.request.errorSending = false;
      this.request.isSending = true;

      const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        }
      };

      axios
        .post('/umbraco/api/wizard/paymentarrangement', this.resultingData, config)
        .then(() => {
          window.location.href = this.successPageUrl;
        })
        .catch(() => {
          this.request.isSending = false;
          this.request.errorSending = true;
        });
    },
  },
};
</script>
